import { useEffect } from "react";
import { normalizeExactTimeControl } from "@chessla/shared";
import { useRememberedPageState } from "../preferences/page-state";
import { useUserSettingsStore } from "../preferences/user-settings.store";
import { PRESETS } from "./analysis-presets";
import { useAnalysisStore } from "./analysis.store";

interface Selection {
  preset: string;
  format: string;
  clock: string;
}
const isSelection = (value: unknown): value is Selection => {
  if (!value || typeof value !== "object") return false;
  const { preset, format, clock } = value as Selection;
  return (
    PRESETS.some((item) => item.id === preset) &&
    ["all", "rapid", "blitz", "bullet", "daily"].includes(format) &&
    typeof clock === "string" &&
    (clock === "all" || clock === "unknown" || normalizeExactTimeControl(clock) === clock)
  );
};

export const useAnalysisPagePreferences = () => {
  const settings = useUserSettingsStore((state) => state.settings);
  const [selection, setSelection] = useRememberedPageState<Selection>(
    "analysis:selection",
    {
      preset: settings.analysisDefaultPreset,
      format: settings.analysisDefaultFormat,
      clock: normalizeExactTimeControl(settings.analysisDefaultClock) ?? "all",
    },
    isSelection,
  );
  const currentPreset = useAnalysisStore((state) => state.preset);
  const busy = useAnalysisStore(
    (state) => state.isRunning || state.jobs.some((job) => job.status === "queued" || job.status === "analyzing"),
  );
  const setPreset = useAnalysisStore((state) => state.setPreset);
  const preset = busy ? currentPreset : (PRESETS.find((item) => item.id === selection.preset) ?? PRESETS[1]);
  useEffect(() => {
    if (!busy && currentPreset.id !== preset.id) setPreset(preset);
  }, [busy, currentPreset.id, preset, setPreset]);
  return { selection, setSelection, preset };
};
